/**
 * Phase 20 — Quote Lines Editor (Flow A).
 *
 * Renders a flat, scannable, table-style editor for an arbitrary number
 * of quote line items. Designed for daily cargo-operatie:
 *   - one-click "Regel toevoegen"
 *   - bulk add N empty rows (admin types 14 → 14 blank rows appear)
 *   - per-row duplicate / delete + multi-select + bulk-delete
 *   - clickable preset chips (zeevracht, THC, VGM, etc.)
 *   - automatic cbm from LxWxH, manually overridable
 *   - server-confirmed totals: this view only previews subtotal optimistically
 *
 * Stability requirements (R4):
 *   - All sub-components defined at MODULE level (no inline functions during render)
 *   - Stable row keys via `_lid` (local id, never persisted)
 *   - No save-on-keystroke — parent commits on submit
 *   - No focus-loss when typing in inputs (verified by 14-char test)
 */
import React, { useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Plus, Trash2, Copy, GripVertical } from "lucide-react";

let _lidCounter = 1;
const _nextLid = () => `lid_${_lidCounter++}`;

export const EMPTY_LINE = () => ({
  _lid: _nextLid(),
  description: "",
  quantity: 1,
  length_cm: "",
  width_cm: "",
  height_cm: "",
  cbm: "",
  weight_kg: "",
  rate: "",
  note: "",
  line_type: "cbm",
  preset_key: "",
});

function num(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function autoCbm(line) {
  if (line.cbm !== "" && line.cbm != null) return num(line.cbm);
  const L = num(line.length_cm), W = num(line.width_cm), H = num(line.height_cm);
  if (L > 0 && W > 0 && H > 0) return Math.round(((L * W * H) / 1_000_000) * 10000) / 10000;
  return 0;
}

function lineTotal(line) {
  const qty = num(line.quantity) || 1;
  const rate = Math.max(0, num(line.rate));
  const lt = (line.line_type || "cbm").toLowerCase();
  if (lt === "fixed") return Math.round(rate * 100) / 100;
  if (lt === "qty") return Math.round(qty * rate * 100) / 100;
  return Math.round(qty * autoCbm(line) * rate * 100) / 100;
}

// -------- Row component (top-level for R4 stability) --------
function LineRow({ line, index, selected, onChange, onToggleSelect, onDuplicate, onDelete, pickup }) {
  const updField = (k, v) => onChange(index, { ...line, [k]: v,
    ...(pickup && k === "cbm" ? { cbm_mode: v === "" ? "auto" : "manual" } : {}) });
  const computedCbm = autoCbm(line);
  const computedTotal = lineTotal(line);

  return (
    <tr
      className={`border-b border-gray-100 ${selected ? "bg-blue-50/40" : "hover:bg-gray-50/40"}`}
      data-testid={`quote-line-row-${index}`}
    >
      <td className="p-1.5 align-top w-8 text-center">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={(e) => onToggleSelect(index, e.target.checked)}
          aria-label="Regel selecteren"
          data-testid={`quote-line-select-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-8 text-center text-xs text-gray-400 font-mono">
        {index + 1}
      </td>
      <td className="p-1.5 align-top min-w-[200px]">
        <Input
          value={line.description}
          onChange={(e) => updField("description", e.target.value)}
          placeholder="Omschrijving"
          className="rounded-none h-8 text-sm"
          data-testid={`quote-line-description-${index}`}
        />
        <Input
          value={line.note || ""}
          onChange={(e) => updField("note", e.target.value)}
          placeholder="Notitie (optioneel)"
          className="rounded-none h-7 text-xs mt-1 text-gray-500"
          data-testid={`quote-line-note-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-20" data-label="Berekening">
        <select
          value={line.line_type || "cbm"}
          onChange={(e) => updField("line_type", e.target.value)}
          className="border border-gray-300 h-8 text-xs px-1 w-full bg-white"
          data-testid={`quote-line-type-${index}`}
        >
          <option value="cbm">m³ × tarief</option>
          <option value="qty">aantal × tarief</option>
          <option value="fixed">vaste post</option>
        </select>
      </td>
      <td className="p-1.5 align-top w-16" data-label="Aantal">
        <Input
          type="number" min="0" step="0.01"
          value={line.quantity}
          onChange={(e) => updField("quantity", e.target.value)}
          className="rounded-none h-8 text-sm text-right"
          data-testid={`quote-line-qty-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-16" data-label="Lengte (cm)">
        <Input
          type="number" min="0" step="0.1"
          value={line.length_cm}
          onChange={(e) => updField("length_cm", e.target.value)}
          placeholder="L"
          className="rounded-none h-8 text-sm text-right"
          data-testid={`quote-line-length-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-16" data-label="Breedte (cm)">
        <Input
          type="number" min="0" step="0.1"
          value={line.width_cm}
          onChange={(e) => updField("width_cm", e.target.value)}
          placeholder="B"
          className="rounded-none h-8 text-sm text-right"
          data-testid={`quote-line-width-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-16" data-label="Hoogte (cm)">
        <Input
          type="number" min="0" step="0.1"
          value={line.height_cm}
          onChange={(e) => updField("height_cm", e.target.value)}
          placeholder="H"
          className="rounded-none h-8 text-sm text-right"
          data-testid={`quote-line-height-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-20" data-label="m³ per collo (auto / handmatig)">
        <Input
          type="number" min="0" step="0.0001"
          value={line.cbm}
          onChange={(e) => updField("cbm", e.target.value)}
          placeholder={computedCbm ? String(computedCbm) : "auto"}
          className="rounded-none h-8 text-sm text-right font-mono"
          data-testid={`quote-line-cbm-${index}`}
        />
        {pickup && <span className="block text-xs mt-1" data-testid={`pickup-line-volume-${index}`}>{(computedCbm * (num(line.quantity) || 1)).toFixed(4)} m³ totaal</span>}
      </td>
      <td className="p-1.5 align-top w-16" data-label="Gewicht (kg)">
        <Input
          type="number" min="0" step="0.01"
          value={line.weight_kg}
          onChange={(e) => updField("weight_kg", e.target.value)}
          placeholder="kg"
          className="rounded-none h-8 text-sm text-right"
          data-testid={`quote-line-weight-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-20" data-label="Tarief (€)">
        <Input
          type="number" min="0" step="0.01"
          value={line.rate}
          onChange={(e) => updField("rate", e.target.value)}
          placeholder="€"
          className="rounded-none h-8 text-sm text-right"
          data-testid={`quote-line-rate-${index}`}
        />
      </td>
      <td className="p-1.5 align-top w-20 font-mono text-right text-sm" data-label="Regelbedrag" data-testid={`quote-line-total-${index}`}>
        € {computedTotal.toFixed(2)}
      </td>
      <td className="p-1.5 align-top w-16 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={() => onDuplicate(index)}
          className="p-1 text-gray-400 hover:text-[#0055FF]"
          title="Regel dupliceren"
          data-testid={`quote-line-duplicate-${index}`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(index)}
          className="p-1 text-gray-400 hover:text-red-500"
          title="Regel verwijderen"
          data-testid={`quote-line-delete-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

// -------- Preset chips (top-level for R4 stability) --------
function PresetChips({ presets, onPick }) {
  if (!presets || presets.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="quote-line-preset-chips">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 mr-1 pt-1.5">
        Snelle regels:
      </span>
      {presets.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => onPick(p)}
          className="px-2.5 py-1 text-xs border border-gray-300 bg-white hover:bg-[#0055FF] hover:text-white hover:border-[#0055FF] rounded-none transition-colors"
          data-testid={`quote-line-preset-${p.key}`}
        >
          + {p.description}
        </button>
      ))}
    </div>
  );
}

// -------- Bulk add input (top-level for R4 stability) --------
function BulkAddBar({ onAdd, onAddOne, onBulkDelete, selectedCount }) {
  const [n, setN] = useState(5);
  return (
    <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-2">
      <Button
        type="button"
        size="sm"
        onClick={onAddOne}
        className="rounded-none bg-[#0055FF] hover:bg-[#0044CC] text-white h-8"
        data-testid="quote-lines-add-one"
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Regel toevoegen
      </Button>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          min="1" max="200"
          value={n}
          onChange={(e) => setN(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
          className="rounded-none h-8 w-16 text-sm"
          data-testid="quote-lines-bulk-count"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onAdd(n)}
          className="rounded-none h-8"
          data-testid="quote-lines-bulk-add"
        >
          lege regels toevoegen
        </Button>
      </div>
      {selectedCount > 0 ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onBulkDelete}
          className="rounded-none h-8 ml-auto border-red-300 text-red-600 hover:bg-red-50"
          data-testid="quote-lines-bulk-delete"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijder {selectedCount} geselecteerd
        </Button>
      ) : null}
    </div>
  );
}

// -------- Main editor component --------
export default function QuoteLineItemsEditor({ lines, onChange, presets = [], pickup = false }) {
  const [selected, setSelected] = useState({});

  const addOne = () => {
    onChange([...(lines || []), EMPTY_LINE()]);
  };
  const addMany = (n) => {
    const fresh = Array.from({ length: n }, () => EMPTY_LINE());
    onChange([...(lines || []), ...fresh]);
  };
  const addPreset = (p) => {
    onChange([
      ...(lines || []),
      { ...EMPTY_LINE(), description: p.description, line_type: p.line_type || "cbm", preset_key: p.key },
    ]);
  };
  const updateRow = (idx, next) => {
    const copy = [...lines];
    copy[idx] = next;
    onChange(copy);
  };
  const deleteRow = (idx) => {
    const copy = lines.filter((_, i) => i !== idx);
    onChange(copy);
    setSelected((s) => {
      const c = { ...s };
      delete c[lines[idx]._lid];
      return c;
    });
  };
  const duplicateRow = (idx) => {
    const copy = [...lines];
    copy.splice(idx + 1, 0, { ...lines[idx], _lid: _nextLid() });
    onChange(copy);
  };
  const toggleSelect = (idx, checked) => {
    const lid = lines[idx]?._lid;
    setSelected((s) => ({ ...s, [lid]: checked }));
  };
  const bulkDelete = () => {
    const remain = (lines || []).filter((l) => !selected[l._lid]);
    onChange(remain);
    setSelected({});
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const subtotal = useMemo(
    () => (lines || []).reduce((s, l) => s + lineTotal(l), 0),
    [lines],
  );

  return (
    <div className="space-y-2" data-testid="quote-lines-editor">
      <PresetChips presets={presets} onPick={addPreset} />
      <BulkAddBar
        onAdd={addMany}
        onAddOne={addOne}
        onBulkDelete={bulkDelete}
        selectedCount={selectedCount}
      />
      <div className="border border-gray-200 overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA] border-b border-gray-300">
            <tr className="text-[10px] uppercase tracking-wide text-gray-600">
              <th className="p-2 w-8">
                <GripVertical className="w-3 h-3 mx-auto text-gray-400" />
              </th>
              <th className="p-2 w-8">#</th>
              <th className="p-2 text-left">Omschrijving</th>
              <th className="p-2">Type</th>
              <th className="p-2">Aantal</th>
              <th className="p-2">L</th>
              <th className="p-2">B</th>
              <th className="p-2">H</th>
              <th className="p-2">m³</th>
              <th className="p-2">Gewicht</th>
              <th className="p-2">Tarief</th>
              <th className="p-2 text-right">Totaal</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {(!lines || lines.length === 0) ? (
              <tr>
                <td colSpan={13} className="p-8 text-center text-gray-500 text-sm">
                  Nog geen regels. Klik op een preset hierboven of "Regel toevoegen".
                </td>
              </tr>
            ) : (
              lines.map((line, idx) => (
                <LineRow
                  pickup={pickup}
                  key={line._lid}
                  line={line}
                  index={idx}
                  selected={!!selected[line._lid]}
                  onChange={updateRow}
                  onToggleSelect={toggleSelect}
                  onDuplicate={duplicateRow}
                  onDelete={deleteRow}
                />
              ))
            )}
          </tbody>
          {lines && lines.length > 0 ? (
            <tfoot className="bg-gray-50 border-t border-gray-300">
              <tr>
                <td colSpan={11} className="p-2 text-right font-bold text-sm uppercase tracking-wide">
                  Subtotaal regels
                </td>
                <td className="p-2 text-right font-mono font-bold text-sm" data-testid="quote-lines-subtotal">
                  € {subtotal.toFixed(2)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
